import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import CloseIcon from '@material-ui/icons/Close'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { fetchProspectAuditDaily, fetchProspectAuditTrends } from 'api'
import { navigationPaths } from 'app/config'
import { showToast } from 'app/helpers'

import InteractionDrawer from 'app/pages/RevenueGoals/InteractionDrawer'

// HQ view: the daily coverage board for non-member prospects. Pick a date and
// see, per centre (Overview) and per visit type (Visit type), how many
// prospects were booked in that day, how many actually turned up, and whether
// the team logged an interaction for them (Expected / Attended / Logged /
// Missed / Coverage %). Coverage is measured against ATTENDED — a no-show
// isn't a follow-up the centre owes. Every number opens the day's visitor
// list, where a miss can be logged on the spot. Opens on today.

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1.5, 3),
    '& .MuiTableCell-sizeSmall': { paddingTop: 6, paddingBottom: 6 }
  },
  muted: { color: theme.palette.text.secondary },
  gapCaption: { color: theme.palette.text.secondary, fontSize: 11, lineHeight: 1.2, whiteSpace: 'nowrap' },
  // Sticky slim header: title + date control in one pinned strip.
  slimBar: {
    position: 'sticky',
    top: 0,
    zIndex: 5,
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    margin: theme.spacing(-1.5, -3, 1),
    padding: theme.spacing(1, 3)
  },
  slimTitleRow: { display: 'flex', alignItems: 'center', gap: 2 },
  slimTitle: { fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' },
  slimGrow: { flex: 1, minWidth: 12 },
  slimWin: { display: 'flex', alignItems: 'center', gap: theme.spacing(1) },
  dateNav: { display: 'flex', alignItems: 'center', gap: 2 },
  dateInput: { width: 150 },
  todayBtn: { textTransform: 'none', marginLeft: theme.spacing(0.5) },
  tabsBar: { marginBottom: theme.spacing(1), borderBottom: '2px solid #eee', minHeight: 40 },
  tableWrap: { marginBottom: theme.spacing(1) },
  segToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap'
  },
  totalsRow: { '& td': { fontWeight: 700, borderTop: `2px solid ${theme.palette.divider}` } },
  clickable: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    fontWeight: 600,
    textDecoration: 'underline',
    textDecorationStyle: 'dotted',
    textUnderlineOffset: '2px',
    '&:hover': { textDecorationStyle: 'solid' }
  },
  chipGap: { backgroundColor: '#fbe3e1', color: '#a6392a', fontWeight: 600 },
  chipFresh: { backgroundColor: '#e3f2e1', color: '#2e6b2b', fontWeight: 600 },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: theme.spacing(2, 3)
  },
  footerNote: { fontSize: 12.5, color: theme.palette.text.secondary, marginTop: theme.spacing(1) },
  emptyBox: { padding: theme.spacing(6, 2), textAlign: 'center' },
  rangeDash: { fontSize: 12, color: '#8a8a8a', margin: '0 2px' },
  trendHead: { display: 'flex', alignItems: 'center', padding: '10px 4px 12px' },
  trendKey: {
    display: 'inline-flex', alignItems: 'center', fontSize: 12, color: '#5C6478',
    '& i': { width: 10, height: 10, borderRadius: 3, display: 'inline-block', marginRight: 6 }
  },
  trendRow: {
    display: 'grid',
    gridTemplateColumns: '160px minmax(120px, 1fr) 108px 56px 104px 78px',
    alignItems: 'center',
    gap: 14,
    padding: '9px 4px',
    borderTop: '1px solid #f0f0f0',
    transition: 'background .15s ease',
    '&:hover': { background: '#fafafa' }
  },
  trendName: { fontSize: 13.5, fontWeight: 600 },
  trendTrack: { height: 9, borderRadius: 5, background: '#f0f0f0', overflow: 'hidden' },
  // The bar grows on mount and on every period change — the movement is what
  // tells you the ranking has been recomputed for the dates you just picked.
  trendFill: { height: '100%', borderRadius: 5, transition: 'width .7s cubic-bezier(.22,.9,.28,1)' },
  trendPct: { fontSize: 14.5, fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  trendMeta: { fontSize: 12, color: '#5C6478', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  chipBad: { backgroundColor: 'rgba(242,94,53,.13)', color: '#C8431F', fontWeight: 600, height: 20 },
  chipOk: { backgroundColor: 'rgba(75,108,183,.12)', color: '#3A558F', fontWeight: 600, height: 20 },
}))

const coveragePctColor = (pct) => {
  if (pct === null || pct === undefined) return '#9e9e9e'
  if (pct >= 80) return '#2E7D32'
  if (pct >= 50) return '#EF6C00'
  return '#C62828'
}

const CoverageCell = ({ pct }) => (
  <span style={{ color: coveragePctColor(pct), fontWeight: 700 }}>
    {pct === null || pct === undefined ? '—' : `${pct}%`}
  </span>
)

// Any number backed by row-level data renders through this — click it, get the
// exact visitors that make it up.
const Clickable = ({ onClick, children }) => {
  const classes = useStyles()
  return (
    <span className={classes.clickable} onClick={onClick} role="button" tabIndex={0}>
      {children}
    </span>
  )
}

// A count in the board: clickable (opens the visitors behind it) when there's
// something to show, greyed out at zero.
const CountCell = ({ n, color, onClick }) =>
  n ? (
    <Clickable onClick={onClick}>
      <span style={color ? { color } : undefined}>{n}</span>
    </Clickable>
  ) : (
    <span style={{ color: '#9e9e9e' }}>0</span>
  )

// Name → the parent's own profile (new tab), so the team can check the real
// CRM interactions behind the row and tell a genuine miss from a data glitch.
// Goes to Search > Parent (the profile itself — wallet, enrolment history,
// interactions), NOT the Users list row it used to land on.
const ProspectNameLink = ({ row }) => (
  <Tooltip title="Open this parent's profile to check the actual interactions">
    <Link
      to={`${navigationPaths.parentdashboard}?parent=${row.parent_user_uuid}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'inherit', borderBottom: '1px dotted #999', textDecoration: 'none' }}
    >
      {row.name}
    </Link>
  </Tooltip>
)

// Opens the shared interaction drawer (POST /crm/interaction/) for this
// prospect — it shows the interactions already logged and lets you add one, so
// it's labelled for what it does rather than "Call log".
const CallLogButton = ({ row, onCallLog }) => (
  <Button
    size="small"
    variant="outlined"
    color="primary"
    style={{ textTransform: 'none' }}
    onClick={() => onCallLog({ uuid: row.parent_user_uuid, name: row.name, phone: row.phone })}
  >
    View interactions
  </Button>
)

// Was an interaction logged for this visitor since their visit?
const YesNoChip = ({ logged }) => {
  const classes = useStyles()
  return (
    <Chip
      size="small"
      label={logged ? 'Yes' : 'No'}
      className={logged ? classes.chipFresh : classes.chipGap}
    />
  )
}

// An active "Declined subscription" mark: the parent said no and keeps taking
// individual sessions. Not chased, never hidden — the chip and its expiry are
// exactly the visibility that makes the mark safe to allow.
const DeclinedChip = ({ row }) => (
  <Tooltip
    title={`Parent has declined a subscription — reminders paused${
      row.declined_expires_on
        ? `, resume ${dayjs(row.declined_expires_on).format('D MMM')}`
        : ''
    }`}
  >
    <Chip
      size="small"
      variant="outlined"
      label="Declined subscription"
      style={{ color: '#5C6270' }}
    />
  </Tooltip>
)

// Composite row identity: a parent can (rarely) appear at two centres the same
// day, so key by parent + centre.
const rowKey = (r) => `${r.parent_user_uuid}|${r.centre_uuid}`

// The working list behind a daily number, shown as a popup. Rows come from the
// live data (via drillRows), so the list updates in place (Missed → Logged) as
// calls are logged, and it STAYS OPEN while the Call-log sidebar is used.
//
// Stacking: the sidebar is a right Drawer at the modal z-index (1300). This
// dialog is pinned just below it (1250) and drops focus enforcement, so the
// sidebar opens cleanly OVER the popup (instead of behind it) and its inputs
// stay focusable — the popup no longer has to close.
const DailyVisitorsDialog = ({ drill, rows, onClose, onCallLog }) => {
  const classes = useStyles()
  const attended = rows.filter((r) => r.attended)
  const logged = attended.filter((r) => r.logged).length
  const declined = attended.filter((r) => !r.logged && r.declined_subscription).length
  const missed = attended.length - logged - declined
  return (
    <Dialog
      open={!!drill}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
      style={{ zIndex: 1250 }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <div>
          <Typography variant="h6">{drill?.title}</Typography>
          <Typography variant="body2" color="textSecondary">
            {rows.length} booked · {attended.length} attended · {logged} logged · {missed} missed
            {declined ? ` · ${declined} declined subscription` : ''}
          </Typography>
        </div>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Visit type</TableCell>
              <TableCell align="center">Attended?</TableCell>
              <TableCell align="center">Logged?</TableCell>
              <TableCell align="right">Interactions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={rowKey(r)} hover>
                <TableCell><ProspectNameLink row={r} /></TableCell>
                <TableCell>
                  {(r.segments || []).join(', ')}
                  {/* The booked course behind the label — so a visit type that
                      looks wrong can be checked without leaving the page. */}
                  {(r.courses || []).length ? (
                    <Typography variant="caption" display="block" color="textSecondary">
                      {r.courses.join(' · ')}
                    </Typography>
                  ) : null}
                  {/* A repeat visitor with an older note is a NEW ask — saying
                      so is what stops "why is she on the list again?". */}
                  {!r.logged && r.last_note_before_visit ? (
                    <Typography variant="caption" display="block" style={{ color: '#C05621' }}>
                      new visit — last note {dayjs(r.last_note_before_visit).format('D MMM')}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="center"><YesNoChip logged={r.attended} /></TableCell>
                <TableCell align="center">
                  {!r.logged && r.declined_subscription
                    ? <DeclinedChip row={r} />
                    : <YesNoChip logged={r.logged} />}
                </TableCell>
                <TableCell align="right"><CallLogButton row={r} onCallLog={onCallLog} /></TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="textSecondary">No visitors.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  )
}

// Page-explanation dialog (the "i" button in the header).
const PageInfoDialog = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>About this page</DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" paragraph>
        <strong>Who&apos;s counted.</strong> Parents who <em>booked</em> one of these visits — Preschool / Afterschool
        Tour, Preschool / Afterschool Trial, Toddler Club, Playdate, Camp, or any other paid one-off session
        (shown as Event) — or <em>walked in</em>, on the chosen date. It reads the bookings directly, so
        <strong>everyone who came is counted, whether or not they later enrolled</strong>.
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>What it checks.</strong> Opens on today; pick any other date top-right. <strong>Expected</strong> = booked
        in for that day. <strong>Attended</strong> = actually turned up (from marked attendance; a walk-in always counts
        as attended). Of those who attended, did the team log an interaction on that day or since?
        <strong> Logged</strong> = yes, <strong>Missed</strong> = no, <strong>Coverage %</strong> = logged ÷ attended.
        No-shows are not counted as misses — they only show in Expected.
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>Overview</strong> groups by centre; <strong>Visit type</strong> groups by visit type (with an
        All-centres / single-centre picker). Click any number — or “View details” — to see the visitors and log a miss on
        the spot. The goal: log an interaction for every prospect who walks in.
      </Typography>
      <Typography variant="body2">
        <strong>Declined subscription.</strong> A parent the team has marked as having said no to a subscription
        (individual paid sessions only) stops getting daily reminders and doesn’t count in Missed or Coverage —
        but stays in every list with a “Declined subscription” chip and the date their mark runs out.
      </Typography>
    </DialogContent>
  </Dialog>
)

// ---------------------------------------------------------------------------
// Trends — coverage per centre over a window, worst first.
//
// The daily board answers "who is owed a follow-up today". It cannot answer
// "which centre has stopped logging": a centre that never logs looks like a
// quiet day until you line the days up.
//
// Ranked, not a twelve-line chart. Twelve series on one axis is unreadable and
// no colour scheme rescues it; the ranking does the work instead, and each row
// carries its own small line so a low number that is RECOVERING can be told
// apart from a low number that is not.
// ---------------------------------------------------------------------------
const COVER_OK = 70

// One centre's weekly coverage as a small line. Weeks with no visits are
// skipped, not drawn as zero — a quiet week is not a failure.
const CoverageSpark = ({ weeks, alert }) => {
  const w = 108
  const h = 26
  const pts = (weeks || [])
    .map((k, i) => (k.coverage == null ? null : { i, v: k.coverage }))
    .filter(Boolean)
  if (!pts.length) return <svg width={w} height={h} aria-hidden="true" />
  const span = Math.max(1, (weeks || []).length - 1)
  const X = (i) => 3 + (i * (w - 6)) / span
  const Y = (v) => h - 3 - (v / 100) * (h - 6)
  const line = pts.map((p) => `${X(p.i).toFixed(1)},${Y(p.v).toFixed(1)}`).join(' ')
  const last = pts[pts.length - 1]
  const stroke = alert ? '#F25E35' : '#4B6CB7'
  return (
    <svg width={w} height={h} role="img" aria-label={`Coverage over ${(weeks || []).length} weeks`}>
      {pts.length > 1 && (
        <polyline points={line} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      )}
      <circle cx={X(last.i)} cy={Y(last.v)} r="3" fill={stroke} stroke="#fff" strokeWidth="1.5" />
    </svg>
  )
}

const TrendsPanel = ({ start, end }) => {
  const classes = useStyles()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let live = true
    setLoading(true)
    fetchProspectAuditTrends(start, end)
      .then((d) => { if (live) setData(d) })
      .catch(() => showToast('Could not load trends', 'error'))
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [start, end])

  const rows = (data?.centres || []).filter((r) => r.attended > 0)

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    )
  }
  if (!rows.length) {
    return (
      <div className={classes.emptyBox}>
        <Typography variant="body1">No attended visits in this period.</Typography>
        <Typography variant="body2" color="textSecondary">
          Widen the dates, or check that centres are marking tour and trial attendance —
          an unmarked visitor reads as a no-show and is not counted here.
        </Typography>
      </div>
    )
  }

  const needs = rows.filter((r) => r.coverage < COVER_OK)

  return (
    <div>
      <div className={classes.trendHead}>
        <Typography variant="body2" className={classes.muted}>
          {dayjs(data.start).format('D MMM')} – {dayjs(data.end).format('D MMM YYYY')}
          {' · '}{rows.length} centres with visits
          {needs.length
            ? ` · ${needs.length} below ${COVER_OK}%`
            : ` · all at or above ${COVER_OK}%`}
        </Typography>
        <span className={classes.slimGrow} />
        <span className={classes.trendKey}>
          <i style={{ background: '#F25E35' }} />Below {COVER_OK}%
          <i style={{ background: '#4B6CB7', marginLeft: 12 }} />On track
        </span>
      </div>

      {rows.map((r) => {
        const alert = r.coverage < COVER_OK
        return (
          <div key={r.centre_uuid} className={classes.trendRow}>
            <div className={classes.trendName}>{r.centre_name}</div>
            <div className={classes.trendTrack}>
              <div
                className={classes.trendFill}
                style={{ width: `${r.coverage}%`, background: alert ? '#F25E35' : '#4B6CB7' }}
              />
            </div>
            <CoverageSpark weeks={r.weeks} alert={alert} />
            <div className={classes.trendPct} style={{ color: alert ? '#F25E35' : 'inherit' }}>
              {r.coverage}%
            </div>
            <div className={classes.trendMeta}>
              {r.missed
                ? <Chip size="small" label={`${r.missed} unlogged`} className={alert ? classes.chipBad : classes.chipOk} />
                : <Chip size="small" label="all logged" className={classes.chipOk} />}
            </div>
            <div className={classes.trendMeta}>{r.logged} of {r.attended}</div>
          </div>
        )
      })}

      <Typography variant="caption" className={classes.muted} component="div" style={{ marginTop: 10 }}>
        Coverage = interactions logged ÷ visitors who attended, over the period. The line is week by week;
        a week with no visits is skipped rather than counted as zero. A centre that never marks attendance
        reads as having no visitors and does not appear here.
      </Typography>
    </div>
  )
}

// `?date=&centre=&parent=` — the deep link Gordon's Slack bullet carries, so
// "log interaction" lands on the visitor's own row with the drawer already
// open, rather than on a board scrolled to today with 12 centres on it. Read
// ONCE at mount: the params are a starting position, not live state, so
// changing the date afterwards must not fight the URL.
const readDeepLink = () => {
  try {
    const q = new URLSearchParams(window.location.search)
    const raw = (q.get('date') || '').trim()
    return {
      date: /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : '',
      centre: (q.get('centre') || '').trim(),
      parent: (q.get('parent') || '').trim()
    }
  } catch (error) {
    return { date: '', centre: '', parent: '' }
  }
}

// `initialTab` + `embedded`: the Centre Ops report (Centre Health > Centre Ops)
// shows this same page inside its Interactions tab, opened on Visit type and
// without the page title, so the report never redraws numbers this page owns.
const InteractionAuditHqRollup = ({ initialTab = 'overview', embedded = false } = {}) => {
  const classes = useStyles()
  const today = dayjs().format('YYYY-MM-DD')
  const [deepLink] = useState(readDeepLink)
  // The board opens on TODAY — it's a daily working list, so the day you open
  // it is the day you mean. If today has no visits the empty state offers a
  // jump to the latest day that does. A deep link overrides it with the visit
  // day the message was about.
  const [date, setDate] = useState(deepLink.date || today)
  const [daily, setDaily] = useState(null)
  const [loading, setLoading] = useState(false)
  const [tab, setTab] = useState(initialTab)
  // Trends reads a window, not a day. One month is the default because it is
  // long enough for a centre's habit to show and short enough that a bad week
  // still moves the number.
  const [trStart, setTrStart] = useState(dayjs().subtract(1, 'month').format('YYYY-MM-DD'))
  const [trEnd, setTrEnd] = useState(dayjs().format('YYYY-MM-DD'))
  const setRange = (months) => {
    setTrEnd(dayjs().format('YYYY-MM-DD'))
    setTrStart(dayjs().subtract(months, 'month').format('YYYY-MM-DD'))
  }
  const [segCentre, setSegCentre] = useState('ALL')
  const [dailyDrill, setDailyDrill] = useState(null)
  const [drawerParent, setDrawerParent] = useState(null)
  const [infoOpen, setInfoOpen] = useState(false)

  const load = useCallback(async (d) => {
    setLoading(true)
    try {
      const res = await fetchProspectAuditDaily(d)
      setDaily(res)
    } catch (error) {
      setDaily(null)
      showToast('Could not load the daily board (HQ access required)', 'error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(date)
  }, [date, load])

  const dailyVisitors = useMemo(() => daily?.visitors || [], [daily])
  const dailyCentres = useMemo(() => daily?.centres || [], [daily])
  const dailySegmentOrder = daily?.segment_order || []
  const latestDate = daily?.latest_visit_date || null
  const shownDate = date || daily?.date || today

  const pct = (logged, total) => (total ? Math.round((1000 * logged) / total) / 10 : null)

  // One tally for every table row (centre, visit type, totals). Expected =
  // booked in. Everything after it is measured on the people who ATTENDED:
  // a no-show isn't a follow-up the centre owes, so it can't drag coverage down.
  const tally = (rows) => {
    const attendedRows = rows.filter((r) => r.attended)
    const loggedRows = attendedRows.filter((r) => r.logged)
    // Declined-subscription parents (marked, unlogged) are deliberately not
    // owed a follow-up: out of Missed and out of the coverage denominator, but
    // still in every visitor list with their own chip — marked is not hidden.
    const declinedRows = attendedRows.filter((r) => !r.logged && r.declined_subscription)
    return {
      rows,
      attendedRows,
      loggedRows,
      missedRows: attendedRows.filter((r) => !r.logged && !r.declined_subscription),
      declinedRows,
      expected: rows.length,
      attended: attendedRows.length,
      logged: loggedRows.length,
      missed: attendedRows.length - loggedRows.length - declinedRows.length,
      declined: declinedRows.length,
      pct: pct(loggedRows.length, attendedRows.length - declinedRows.length)
    }
  }

  // Inline drill (no popup): remember WHICH visitors were clicked (by key), then
  // re-derive the list from the live data every render — so after a call is
  // logged and the board refreshes, the drill list updates in place (Missed →
  // Logged) without reopening anything.
  const showDaily = (title, rows) => setDailyDrill({ title, keys: new Set(rows.map(rowKey)) })
  // Call log opens the sidebar OVER the page; the inline list stays put.
  const callLog = (parent) => setDrawerParent(parent)
  const goDate = (d) => {
    setDailyDrill(null)
    setDate(d)
  }
  const drillRows = useMemo(
    () => (dailyDrill ? dailyVisitors.filter((v) => dailyDrill.keys.has(rowKey(v))) : []),
    [dailyDrill, dailyVisitors]
  )

  // Overview = one row per centre (all scoped centres shown, even at 0 visits).
  const overviewRows = useMemo(() => {
    const acc = {}
    dailyCentres.forEach((c) => {
      acc[c.centre_uuid] = { centre_uuid: c.centre_uuid, centre_name: c.centre_name, rows: [] }
    })
    dailyVisitors.forEach((v) => {
      const b = acc[v.centre_uuid] || (acc[v.centre_uuid] = { centre_uuid: v.centre_uuid, centre_name: v.centre_name, rows: [] })
      b.rows.push(v)
    })
    return Object.values(acc)
      .map((b) => ({ ...b, ...tally(b.rows) }))
      .sort((a, b) => b.missed - a.missed || b.attended - a.attended || a.centre_name.localeCompare(b.centre_name))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyVisitors, dailyCentres])

  const segVisitors = useMemo(
    () => (segCentre === 'ALL' ? dailyVisitors : dailyVisitors.filter((v) => v.centre_uuid === segCentre)),
    [dailyVisitors, segCentre]
  )
  const segmentRows = useMemo(() => {
    const acc = {}
    segVisitors.forEach((v) => {
      ;(v.segments || []).forEach((seg) => {
        const b = acc[seg] || (acc[seg] = { segment: seg, rows: [] })
        b.rows.push(v)
      })
    })
    return Object.values(acc)
      .map((b) => ({ ...b, ...tally(b.rows) }))
      .sort((a, b) => {
        const ia = dailySegmentOrder.indexOf(a.segment)
        const ib = dailySegmentOrder.indexOf(b.segment)
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segVisitors, dailySegmentOrder])

  // Totals rows: distinct visitors (a multi-segment visitor counts once).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const overviewTotals = useMemo(() => tally(dailyVisitors), [dailyVisitors])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const segTotals = useMemo(() => tally(segVisitors), [segVisitors])

  const hasVisitors = dailyVisitors.length > 0
  const dLabel = dayjs(shownDate).format('D MMM')

  // Land a deep link on its visitor: open that centre's list and the drawer.
  // Fires ONCE (`deepLinkDone`) — the board reloads after every save, and
  // re-opening the drawer on top of the refresh would trap whoever just closed
  // it. A parent who isn't on the board that day is left alone with the list
  // open, which is a truer answer than a toast: the row moved or the date is
  // wrong, and both are visible from there.
  const [deepLinkDone, setDeepLinkDone] = useState(!deepLink.parent)
  useEffect(() => {
    if (deepLinkDone || loading || !daily) return
    setDeepLinkDone(true)
    const centre = deepLink.centre
      ? overviewRows.find((c) => c.centre_uuid === deepLink.centre)
      : null
    if (centre)
      showDaily(`${centre.centre_name} — booked in ${dLabel}`, centre.rows)
    const row = dailyVisitors.find(
      (v) =>
        v.parent_user_uuid === deepLink.parent &&
        (!deepLink.centre || v.centre_uuid === deepLink.centre)
    )
    if (row)
      callLog({ uuid: row.parent_user_uuid, name: row.name, phone: row.phone })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daily, loading, deepLinkDone])

  const EmptyState = () => (
    <div className={classes.emptyBox}>
      <Typography variant="body1" gutterBottom>
        No prospect visits recorded on {dayjs(shownDate).format('D MMM YYYY')}.
      </Typography>
      {latestDate && latestDate !== shownDate ? (
        <Button variant="outlined" color="primary" style={{ textTransform: 'none' }} onClick={() => goDate(latestDate)}>
          Jump to the latest day with visits ({dayjs(latestDate).format('D MMM YYYY')})
        </Button>
      ) : (
        <Typography variant="body2" color="textSecondary">
          Try another date, or check that prospect visits are being logged for these centres.
        </Typography>
      )}
    </div>
  )

  return (
    <div className={classes.root}>
      <div className={classes.slimBar}>
        <div className={classes.slimTitleRow}>
          {!embedded && <Typography className={classes.slimTitle}>Interactions Audit</Typography>}
          <Tooltip title="What is this page?" arrow>
            <IconButton size="small" onClick={() => setInfoOpen(true)} aria-label="About this page">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <span className={classes.slimGrow} />
        {tab === 'trends' ? (
          <div className={classes.slimWin}>
            <span className={classes.gapCaption}>Period</span>
            <div className={classes.dateNav}>
              <TextField
                type="date"
                size="small"
                variant="outlined"
                value={trStart}
                onChange={(e) => e.target.value && setTrStart(e.target.value)}
                inputProps={{ max: trEnd }}
                className={classes.dateInput}
              />
              <span className={classes.rangeDash}>to</span>
              <TextField
                type="date"
                size="small"
                variant="outlined"
                value={trEnd}
                onChange={(e) => e.target.value && setTrEnd(e.target.value)}
                inputProps={{ min: trStart, max: today }}
                className={classes.dateInput}
              />
              <Button size="small" className={classes.todayBtn} onClick={() => setRange(1)}>
                1 month
              </Button>
              <Button size="small" className={classes.todayBtn} onClick={() => setRange(3)}>
                3 months
              </Button>
            </div>
          </div>
        ) : (
        <div className={classes.slimWin}>
          <span className={classes.gapCaption}>Visit date</span>
          <div className={classes.dateNav}>
            <Tooltip title="Previous day" arrow>
              <IconButton
                size="small"
                aria-label="Previous day"
                onClick={() => goDate(dayjs(shownDate).subtract(1, 'day').format('YYYY-MM-DD'))}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <TextField
              type="date"
              size="small"
              variant="outlined"
              value={shownDate}
              onChange={(e) => e.target.value && goDate(e.target.value)}
              inputProps={{ max: today }}
              className={classes.dateInput}
            />
            <Tooltip title="Next day" arrow>
              <span>
                <IconButton
                  size="small"
                  aria-label="Next day"
                  disabled={shownDate >= today}
                  onClick={() => goDate(dayjs(shownDate).add(1, 'day').format('YYYY-MM-DD'))}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {shownDate !== today && (
              <Button size="small" className={classes.todayBtn} onClick={() => goDate(today)}>
                Today
              </Button>
            )}
          </div>
        </div>
        )}
      </div>

      {/* The tab strip sits OUTSIDE the daily-data gate on purpose: Trends reads
          a window, not a day, so an empty today must not hide it. */}
      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabsBar}
      >
        <Tab value="overview" label="Overview" style={{ textTransform: 'none' }} />
        <Tab value="segment" label="Visit type" style={{ textTransform: 'none' }} />
        <Tab value="trends" label="Trends" style={{ textTransform: 'none' }} />
      </Tabs>

      {tab === 'trends' ? (
        <TrendsPanel start={trStart} end={trEnd} />
      ) : loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : !daily ? (
        <Typography variant="body2" className={classes.muted}>
          Nothing to show.
        </Typography>
      ) : (
        <>

          {tab === 'overview' && (
            !hasVisitors ? (
              <EmptyState />
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined" className={classes.tableWrap}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Centre</TableCell>
                        <TableCell align="right">Expected</TableCell>
                        <TableCell align="right">Attended</TableCell>
                        <TableCell align="right">Logged</TableCell>
                        <TableCell align="right">Missed</TableCell>
                        <TableCell align="right">Coverage</TableCell>
                        <TableCell align="center">Interactions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {overviewRows.map((c) => (
                        <TableRow key={c.centre_uuid} hover>
                          <TableCell>{c.centre_name}</TableCell>
                          <TableCell align="right">
                            <CountCell
                              n={c.expected}
                              onClick={() => showDaily(`${c.centre_name} — booked in ${dLabel}`, c.rows)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <CountCell
                              n={c.attended}
                              onClick={() => showDaily(`${c.centre_name} — attended ${dLabel}`, c.attendedRows)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <CountCell
                              n={c.logged}
                              color="#2E7D32"
                              onClick={() => showDaily(`${c.centre_name} — logged (${dLabel})`, c.loggedRows)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <CountCell
                              n={c.missed}
                              color="#C62828"
                              onClick={() => showDaily(`${c.centre_name} — missed (${dLabel})`, c.missedRows)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <CoverageCell pct={c.pct} />
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              style={{ textTransform: 'none' }}
                              disabled={!c.expected}
                              onClick={() => showDaily(`${c.centre_name} — booked in ${dLabel}`, c.rows)}
                            >
                              View details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className={classes.totalsRow}>
                        <TableCell>All centres</TableCell>
                        <TableCell align="right">
                          <CountCell
                            n={overviewTotals.expected}
                            onClick={() => showDaily(`All centres — booked in ${dLabel}`, dailyVisitors)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <CountCell
                            n={overviewTotals.attended}
                            onClick={() => showDaily(`All centres — attended ${dLabel}`, overviewTotals.attendedRows)}
                          />
                        </TableCell>
                        <TableCell align="right"><span style={{ color: '#2E7D32' }}>{overviewTotals.logged}</span></TableCell>
                        <TableCell align="right"><span style={{ color: '#C62828' }}>{overviewTotals.missed}</span></TableCell>
                        <TableCell align="right"><CoverageCell pct={overviewTotals.pct} /></TableCell>
                        <TableCell align="center" />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography className={classes.footerNote}>
                  Expected = parents booked in for a tour / trial / toddler slot (or who walked in) on {dayjs(shownDate).format('D MMM YYYY')} — everyone booked, whether or not they later enrolled.
                  Attended = marked present that day (a walk-in counts as attended). Logged = an interaction was put on/after the visit day.
                  Coverage = logged ÷ attended, so no-shows don’t count against a centre. Click any number, or “View details”, to see the visitors and log a miss.
                </Typography>
              </>
            )
          )}

          {tab === 'segment' && (
            <>
              <div className={classes.segToolbar}>
                <span className={classes.gapCaption}>Centre</span>
                <Select
                  value={segCentre}
                  onChange={(e) => setSegCentre(e.target.value)}
                  variant="outlined"
                  margin="dense"
                  style={{ minWidth: 200 }}
                >
                  <MenuItem value="ALL">All centres</MenuItem>
                  {dailyCentres.map((c) => (
                    <MenuItem key={c.centre_uuid} value={c.centre_uuid}>{c.centre_name}</MenuItem>
                  ))}
                </Select>
              </div>
              {segmentRows.length === 0 ? (
                <EmptyState />
              ) : (
                <>
                  <TableContainer component={Paper} variant="outlined" className={classes.tableWrap}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Visit type</TableCell>
                          <TableCell align="right">Expected</TableCell>
                          <TableCell align="right">Attended</TableCell>
                          <TableCell align="right">Logged</TableCell>
                          <TableCell align="right">Missed</TableCell>
                          <TableCell align="right">Coverage</TableCell>
                          <TableCell align="center">Interactions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {segmentRows.map((s) => (
                          <TableRow key={s.segment} hover>
                            <TableCell>{s.segment}</TableCell>
                            <TableCell align="right">
                              <CountCell
                                n={s.expected}
                                onClick={() => showDaily(`${s.segment} — booked in ${dLabel}`, s.rows)}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <CountCell
                                n={s.attended}
                                onClick={() => showDaily(`${s.segment} — attended ${dLabel}`, s.attendedRows)}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <CountCell
                                n={s.logged}
                                color="#2E7D32"
                                onClick={() => showDaily(`${s.segment} — logged (${dLabel})`, s.loggedRows)}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <CountCell
                                n={s.missed}
                                color="#C62828"
                                onClick={() => showDaily(`${s.segment} — missed (${dLabel})`, s.missedRows)}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <CoverageCell pct={s.pct} />
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                style={{ textTransform: 'none' }}
                                disabled={!s.expected}
                                onClick={() => showDaily(`${s.segment} — booked in ${dLabel}`, s.rows)}
                              >
                                View details
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className={classes.totalsRow}>
                          <TableCell>All visit types</TableCell>
                          <TableCell align="right">{segTotals.expected}</TableCell>
                          <TableCell align="right">{segTotals.attended}</TableCell>
                          <TableCell align="right"><span style={{ color: '#2E7D32' }}>{segTotals.logged}</span></TableCell>
                          <TableCell align="right"><span style={{ color: '#C62828' }}>{segTotals.missed}</span></TableCell>
                          <TableCell align="right"><CoverageCell pct={segTotals.pct} /></TableCell>
                          <TableCell align="center" />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography className={classes.footerNote}>
                    {segCentre === 'ALL' ? 'All centres' : (dailyCentres.find((c) => c.centre_uuid === segCentre)?.centre_name || 'Centre')}
                    {' · '}visits on {dayjs(shownDate).format('D MMM YYYY')}. A visitor with more than one visit type is counted under each; the total counts them once.
                  </Typography>
                </>
              )}
            </>
          )}
        </>
      )}

      <DailyVisitorsDialog
        drill={dailyDrill}
        rows={drillRows}
        onClose={() => setDailyDrill(null)}
        onCallLog={callLog}
      />

      <InteractionDrawer
        parent={drawerParent}
        onClose={() => setDrawerParent(null)}
        onLogged={() => load(date)}
        allowDeclinedMark
      />

      <PageInfoDialog open={infoOpen} onClose={() => setInfoOpen(false)} />
    </div>
  )
}

export default InteractionAuditHqRollup
